/**
 * The in-app notification bell (project brief §21, in-app channel): an
 * unread badge, a dropdown of the most recent notifications, mark-one/
 * mark-all read, and a link to each notification's ticket or design request (or to the full
 * `/notifications` page). Unread count is polled — see `useUnreadNotificationCount`
 * in `api/notifications.ts` for the interval/visibility/focus behaviour.
 */
import { useRef, useState, type SVGProps } from 'react';
import { useNavigate } from 'react-router-dom';
import { useEscapeKey, useOnClickOutside } from '../../ui/hooks';
import { IconButton } from '../../ui/IconButton/IconButton';
import { Spinner } from '../../ui/Spinner/Spinner';
import {
  useMarkAllNotificationsRead,
  useMarkNotificationRead,
  useNotificationList,
  useUnreadNotificationCount,
} from '../../api/notifications';
import { cx } from '../../ui/cx';
import styles from './NotificationBell.module.css';

function BellIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" {...props}>
      <path d="M4 7a5 5 0 0 1 10 0c0 3.5 1 4.5 1 4.5H3S4 10.5 4 7Z" />
      <path d="M7.5 14a1.5 1.5 0 0 0 3 0" />
    </svg>
  );
}

function timeAgo(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime();
  const min = Math.round(ms / 60000);
  if (min < 1) return 'just now';
  if (min < 60) return `${min}m ago`;
  const hr = Math.round(min / 60);
  if (hr < 24) return `${hr}h ago`;
  return `${Math.round(hr / 24)}d ago`;
}

export interface NotificationBellProps {
  /** Extra class for the bell trigger button (lets a host, e.g. the top bar, size it). */
  triggerClassName?: string | undefined;
  /** Extra class for the unread-count badge. */
  badgeClassName?: string | undefined;
}

export function NotificationBell({ triggerClassName, badgeClassName }: NotificationBellProps = {}) {
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  const unread = useUnreadNotificationCount();
  const list = useNotificationList({ limit: 8 });
  const markRead = useMarkNotificationRead();
  const markAllRead = useMarkAllNotificationsRead();

  useOnClickOutside(wrapperRef, () => setOpen(false), open);
  useEscapeKey(() => setOpen(false), open);

  const count = unread.data?.count ?? 0;

  return (
    <div className={styles.wrapper} ref={wrapperRef}>
      <IconButton
        aria-label={count > 0 ? `Notifications, ${count} unread` : 'Notifications'}
        icon={<BellIcon />}
        className={triggerClassName}
        active={open}
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        aria-haspopup="true"
      />
      {count > 0 && (
        <span className={cx(styles.badge, badgeClassName)} aria-hidden="true">
          {count > 99 ? '99+' : count}
        </span>
      )}

      {open && (
        <div className={styles.panel} role="menu" aria-label="Notifications">
          <div className={styles.panelHeader}>
            <p className={styles.panelTitle}>Notifications</p>
            <button
              type="button"
              className={styles.markAll}
              disabled={count === 0 || markAllRead.isPending}
              onClick={() => markAllRead.mutate()}
            >
              Mark all read
            </button>
          </div>

          {list.isLoading ? (
            <div className={styles.empty}>
              <Spinner size="sm" label="Loading notifications" />
            </div>
          ) : list.isError ? (
            <p className={styles.empty}>Couldn&apos;t load notifications.</p>
          ) : !list.data || list.data.data.length === 0 ? (
            <p className={styles.empty}>You&apos;re all caught up.</p>
          ) : (
            list.data.data.map((n) => (
              <button
                key={n.id}
                type="button"
                role="menuitem"
                className={`${styles.item} ${n.readAt === null ? styles.itemUnread : ''}`}
                onClick={() => {
                  if (n.readAt === null) markRead.mutate(n.id);
                  setOpen(false);
                  if (n.ticketId) navigate(`/tickets/${n.ticketId}`);
                  else if (n.designRequestId) navigate(`/design-workflow/${n.designRequestId}`);
                }}
              >
                <span className={styles.itemTitle}>
                  {n.readAt === null && <span className={styles.unreadDot} aria-hidden="true" />}
                  {n.title}
                </span>
                {n.body && <span className={styles.itemBody}>{n.body}</span>}
                <span className={styles.itemMeta}>{timeAgo(n.createdAt)}</span>
              </button>
            ))
          )}

          <div className={styles.footer}>
            <a
              className={styles.footerLink}
              href="/notifications"
              onClick={(e) => {
                e.preventDefault();
                setOpen(false);
                navigate('/notifications');
              }}
            >
              View all notifications
            </a>
          </div>
        </div>
      )}
    </div>
  );
}
